import { ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { ITimekitComponent } from '../timekit-tabs/timekit-tabs.component';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ApiService } from 'src/app/api.service';
import { ThemingService } from 'src/app/theming.service';
import { CalendarOptions } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import esLocale from '@fullcalendar/core/locales/es';
import bootstrap5Plugin from '@fullcalendar/bootstrap5';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { cu } from '@fullcalendar/core/internal-common';
import { LambdaService } from 'src/app/lambda.service';

interface Project {
  timekitProjectId: number;
  shopId: number;
  timekitResourceId: number | string;
  projectId: string;
  projectName: string;
  location: string;
  externalId?: string;
  projectType: string;
}

interface AvailabilityResponse {
  body: string;
}

interface Client {
  firstName: string;
  lastName: string;
  email?: string;
  nationalId?: string;
  remainingSessions?: number;
  scheduledSessions?: number;
  itemId?: number;
  productId?: number;
  saleId?: number;
  clientId?: number;
  phone?: string;
  totalSessions?: number;
  quantitySessions?: number;
  sales?: any[];
  selectedSale?: any;
}

@Component({
  selector: 'app-timekit-bookings',
  templateUrl: './timekit-bookings.component.html',
  styleUrls: ['./timekit-bookings.component.scss']
})

export class TimekitBookingsComponent implements ITimekitComponent, OnInit {
  @Input() showModal: boolean = false;
  @Input() data: any;
  @ViewChild('calendar') calendarComponent: any;
  public projects: Project[] = [];
  public selectedProjectId: string = '';
  public bookingForm: FormGroup;
  public selectedEvent: any = null;
  public isSuccessModalVisible: boolean = false;
  public isErrorModalVisible: boolean = false;
  resources: any[] = [];
  projectResources: any[] = [];
  selectedResourceId: string = '';
  loading = true;
  clients: Client[] = [];
  totalAmount: number = 0;
  searchString: string = '';
  order: string = 'DESC';
  amount: number = 20;
  currentPage: number = 1;
  showClientModal = false;
  searchSubject = new Subject<string>();
  searchControl = new FormControl('');
  externalId: string = '';
  showStatusModal = false;
  statusModalType: 'loading' | 'success' = 'loading';
  statusModalMessage = '';
  eventDates: Set<string> = new Set();
  modalCancelHours = false;
  pendingBlockData: { start: Date; end: Date; dayOfWeek: string } | null = null;
  selectedClient: Client | null = null;
  canSchedulePrivate: boolean = true;
  availableSessions: number = 0;
  isCheckingSessions = false;

  constructor(
    private el: ElementRef,
    private fb: FormBuilder,
    private apiService: ApiService,
    public themingService: ThemingService,
    private lambdaService: LambdaService,
    private cdr: ChangeDetectorRef
  ) {
    this.bookingForm = this.fb.group({
      customerName: ['', Validators.required],
      customerEmail: ['', Validators.required],
      customerNationalId: ['', Validators.required],
      customerPhone: ['', Validators.required],
    });
  }

  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, timeGridPlugin, bootstrap5Plugin, momentTimezonePlugin],
    timeZone: 'America/Costa_Rica',
    themeSystem: 'bootstrap5',
    initialView: 'dayGridMonth',
    locale: esLocale,
    events: [],
    eventTimeFormat: {
      hour: 'numeric',
      minute: '2-digit',
      meridiem: true,
    },
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'dayGridMonth,timeGridWeek,timeGridDay,listMonth'
    },
    eventContent: (arg) => {
      const startTime =
        arg.event.start?.toLocaleTimeString('es-CR', { hour: 'numeric' }) || '';
      const endTime =
        arg.event.end?.toLocaleTimeString('es-CR', { hour: 'numeric' }) || '';
      return {
        html: `<div title="${startTime} - ${endTime}">
                 ${startTime} - ${endTime}
               </div>`,

      };
    },
    eventClick: this.onEventClick.bind(this),
    dayCellContent: (arg) => {
      const dayNum = arg.dayNumberText;
      const dateStr = arg.date.toISOString().split('T')[0];
      const showLock = this.eventDates.has(dateStr);

      const wrapper = document.createElement('div');
      wrapper.style.display = 'flex';
      wrapper.style.alignItems = 'center';
      wrapper.style.justifyContent = 'flex-start';
      wrapper.style.gap = '4px';

      if (showLock) {
        const lock = document.createElement('button');
        lock.innerText = '🔒';
        lock.title = 'Bloquear todo el día';
        lock.style.border = 'none';
        lock.style.background = 'transparent';
        lock.style.cursor = 'pointer';
        lock.style.fontSize = '14px';

        lock.onclick = (e) => {
          e.preventDefault();
          e.stopPropagation();

          const start = new Date(arg.date);
          const end = new Date(arg.date);
          start.setHours(0, 0, 0, 0);
          end.setHours(23, 59, 59, 999);
          const dayOfWeek = start.toLocaleDateString('en-US', { weekday: 'long' });

          this.pendingBlockData = { start, end, dayOfWeek };
          this.modalCancelHours = true;
        };

        wrapper.appendChild(lock);
      }

      const dayText = document.createElement('span');
      dayText.innerText = dayNum;
      dayText.style.fontSize = '13px';

      wrapper.appendChild(dayText);

      return { domNodes: [wrapper] };
    }
  };

  ngOnInit(): void {
    this.initializeComponent();
    this.searchString = '';
    this.getClientSessions();

    this.searchControl.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe(value => {
        this.searchString = value || '';
        this.getClientSessions();
      });
  }

  async initializeComponent() {
    try {
      await this.getTimekitResources();
      await this.getTimekitProjects();
    } catch (error) {
      console.error('Error initializing component:', error);
    }
  }

  openClientModal() {
    this.showClientModal = true;
    this.getClientSessions();
  }

  closeClientModal() {
    this.showClientModal = false;
  }

  onSearchInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const value = inputElement.value;
    this.searchSubject.next(value);
  }

  async getClientSessions() {
    this.loading = true;

    try {
      const rawData: any = await this.lambdaService.getClientSessions(this.themingService.shopId);
      let clientsArray = [];

      if (Array.isArray(rawData)) {
        const found = rawData.find((entry: any) => Array.isArray(entry));
        clientsArray = found || [];
      }

      const search = this.searchString.toLowerCase();
      this.clients = clientsArray.filter((client: Client) =>
        (client.firstName + ' ' + client.lastName).toLowerCase().includes(search)
      );

    } catch (error) {
      console.error('Error al obtener clientes:', error);
      this.clients = [];
    }

    this.loading = false;
  }

  async selectClient(client: Client) {
    this.selectedClient = client;
    this.bookingForm.patchValue({
      customerName: `${client.firstName} ${client.lastName}`,
      customerEmail: client.email,
      customerNationalId: client.nationalId,
      customerPhone: client.phone
    });
    this.closeClientModal();

    this.canSchedulePrivate = false;
    this.availableSessions = 0;
    this.isCheckingSessions = true;
    await this.loadClientSales(client);

    this.cdr.detectChanges();
  }

  async loadClientSales(client: Client) {
    if (!client || !client.nationalId) return;

    try {
      this.loading = true;
      this.isCheckingSessions = true;
      this.canSchedulePrivate = false;
      this.availableSessions = 0;

      const shopId = this.themingService.shopId;
      const counter = 0;
      const amount = 20;
      const searchString = '';

      const response: any = await this.lambdaService.getClientSales(
        client.nationalId ?? '0',
        counter,
        amount,
        searchString,
        shopId
      );

      const allSales = Array.isArray(response)
        ? response
        : JSON.parse(response.body || '[]');

      const paidSales = allSales.filter((sale: any) => sale.saleState === 5);
      let totalAvailableSessions = 0;

      for (const sale of paidSales) {
        const saleId = sale.saleId;

        const detailsResponse: any = await this.lambdaService.getUserSaleById(saleId, shopId);

        let details: any;
        try {
          if (detailsResponse?.saleInfo && detailsResponse?.items) {
            details = detailsResponse;
          } else if (detailsResponse?.body) {
            const parsed = JSON.parse(detailsResponse.body);
            details = parsed?.saleInfo && parsed?.items ? parsed : parsed[0] || parsed;
          } else if (Array.isArray(detailsResponse) && detailsResponse[0]?.items) {
            details = detailsResponse[0];
          } else {
            details = detailsResponse;
          }
        } catch (err) {
          console.warn('Error al parsear detalles de la venta', saleId, err);
          details = detailsResponse;
        }

        const items = details?.items || [];

        for (const item of items) {
          if (
            typeof item.productSessions === 'number' &&
            typeof item.bookingSessions === 'number' &&
            typeof item.saleItemQuantity === 'number'
          ) {
            const totalSessionsForItem = item.productSessions * item.saleItemQuantity;
            if (item.bookingSessions < totalSessionsForItem) {
              const available = totalSessionsForItem - item.bookingSessions;
              totalAvailableSessions += available;
            }
          }
        }
      }

      this.availableSessions = totalAvailableSessions;
      this.canSchedulePrivate = totalAvailableSessions > 0;

    } catch (error) {
      console.error('Error al obtener ventas del cliente:', error);
    } finally {
      this.loading = false;
      this.isCheckingSessions = false;
      this.cdr.detectChanges();
    }
  }

  async selectSale(saleId: number) {
    try {
      this.loading = true;
      const shopId = this.themingService.shopId;

      const saleDetails: any = await this.lambdaService.getUserSaleById(saleId, shopId);
      const parsed = Array.isArray(saleDetails)
        ? saleDetails[0]
        : JSON.parse(saleDetails.body || '{}');
      if (this.selectedClient) {
        this.selectedClient.selectedSale = parsed;
      }
    } catch (error) {
      console.error('Error al obtener detalle de la venta:', error);
    } finally {
      this.loading = false;
    }
  }

  async getTimekitProjects() {
    try {
      const response = await this.lambdaService.getTimekitProjects(this.themingService.shopId) as { data: Project[] };
      this.projects = response.data || [];

      if (this.projects.length > 0) {
        this.selectedProjectId = this.projects[0].projectId;
        this.extractResourceIdFromProjectId(this.selectedProjectId);
        await this.getBookings();
      }

    } catch (error) {
      console.error('Error fetching projects:', error);
      this.projects = [];
    }
  }

  async getTimekitResources() {
    this.loading = true;
    try {
      const response = await this.lambdaService.getTimekitResources(this.themingService.shopId) as { data: { resourceId: string, resourceName: string }[] };
      this.resources = response.data || [];
    } catch (error) {
      console.error('Error fetching resources:', error);
      this.resources = [];
    } finally {
      this.loading = false;
    }
  }

  isPrivateProjectWithoutSessions(): boolean {
    if (!this.selectedProjectId) return false;

    const project = this.projects.find(p => p.projectId === this.selectedProjectId);
    if (!project) return false;

    const isPrivate = project.projectType?.toLowerCase().trim() === 'private';
    return isPrivate && !this.canSchedulePrivate;
  }

  get isPrivateProject(): boolean {
    const project = this.projects?.find(p => p.projectId === this.selectedProjectId);
    if (!project || !project.projectType) return false;
    return project.projectType.toLowerCase().trim() === 'private';
  }

  get isScheduleDisabled(): boolean {
    return !this.bookingForm.valid || (this.isPrivateProject && !this.canSchedulePrivate);
  }

  onProjectChange(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    this.selectedProjectId = selectElement.value;
    this.extractResourceIdFromProjectId(this.selectedProjectId);
    this.getBookings();
  }

  onResourceChange(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    this.selectedResourceId = selectElement.value;
    this.getBookings();
  }

  extractResourceIdFromProjectId(projectId: string) {
    const selectedProject = this.projects.find(p => p.projectId === projectId);
    if (!selectedProject) {
      console.error('No se encontró el proyecto seleccionado:', projectId);
      return;
    }

    const resourceIds = selectedProject.timekitResourceId
      .toString()
      .split(',')
      .map(id => parseInt(id.trim(), 10));

    this.projectResources = this.resources.filter(r =>
      resourceIds.includes(r.timekitResourceId)
    );

    if (this.projectResources.length > 0) {
      this.selectedResourceId = this.projectResources[0].resourceId;
    } else {
      console.warn('No matching resources found for timekitResourceIds:', resourceIds);
    }
  }

  async getBookings() {
    try {

      const shopId = this.themingService.shopId;
      if (!shopId || !this.selectedProjectId || !this.selectedResourceId) {
        console.error('Shop ID o Project ID is not defined');
        return;
      }

      const response = (await this.apiService.getBookings(
        shopId,
        this.selectedProjectId,
        this.selectedResourceId
      )) as any;

      const availabilityData = Array.isArray(response) ? response : JSON.parse(response.body || "[]");

      this.eventDates.clear();

      const events = availabilityData.map((event: any) => {
        const resource = event.resources?.[0];
        const resourceId = resource?.id || null;
        const resourceName = resource?.name || 'Sin nombre';

        const dateStr = new Date(event.start).toISOString().split('T')[0];
        this.eventDates.add(dateStr);

        return {
          start: event.start,
          end: event.end,
          title: resourceName,
          classNames: ['blue-event'],
          extendedProps: {
            resourceId,
            resourceName,
            ...event,
          },
        };
      });

      // Usar la API de Full Calendar para actualizar eventos
      try {
        const calendarApi = (this.calendarComponent as any)?.api || (this.calendarComponent as any)?.getApi?.();

        if (calendarApi) {
          // Remover todos los eventos actuales
          calendarApi.removeAllEvents();

          // Agregar los nuevos eventos
          events.forEach((event: any) => {
            calendarApi.addEvent(event);
          });
        } else {
          // Crear un nuevo array para forzar la detección de cambios
          this.calendarOptions = Object.assign({}, this.calendarOptions, {
            events: [...events]
          });
        }
      } catch (error) {
        console.error('Error actualizando Full Calendar:', error);

        // Fallback final
        this.calendarOptions = Object.assign({}, this.calendarOptions, {
          events: [...events]
        });
      }

      this.cdr.detectChanges();

    } catch (error) {
      console.error('Error fetching availability:', error);
    }
  }

  onEventClick(info: any) {
    const eventDetails = info.event.extendedProps;
    const resourceId = eventDetails.resource_id;

    const formattedDate = this.getFormattedDate(info.event.start);
    const formattedStartTime = this.getFormattedTime(info.event.start);
    const formattedEndTime = this.getFormattedTime(info.event.end);

    const dayOfWeek = new Date(info.event.start).toLocaleDateString('en-US', { weekday: 'long' });

    this.selectedEvent = {
      ...eventDetails,
      start: info.event.start,
      end: info.event.end,
      resource_id: resourceId,
      formattedDate,
      formattedTime: `${formattedStartTime} - ${formattedEndTime}`,
      dayOfWeek,
    };
    this.showModal = true;
  }

  getFormattedDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { weekday: 'long', month: 'long', day: 'numeric' });
  }

  getFormattedTime(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  formatDateToTimekit(date: string | Date): string {
    const d = new Date(date);
    const isoString = d.toISOString();
    return isoString.replace(/\.\d{3}Z$/, '+00:00');
  }

  formatToTimekitEventDate(date: string | Date): string {
    const localDate = new Date(date.toLocaleString('en-US', { timeZone: 'America/Costa_Rica' }));
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    const hours = String(localDate.getHours()).padStart(2, '0');
    const minutes = String(localDate.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  }

  createBookings() {
    if (!this.selectedEvent || !this.bookingForm.valid) {
      console.error('Evento seleccionado o formulario no válido');
      return;
    }

    const shopId = this.themingService.shopId;
    const resourceId = this.selectedResourceId;
    const projectId = this.selectedProjectId;

    if (!shopId) {
      console.error('Shop ID no está disponible');
      this.closeStatusModalWithError('Error: Shop ID no disponible');
      return;
    }

    if (!resourceId) {
      console.error('Resource ID no está disponible');
      this.closeStatusModalWithError('Error: Resource ID no disponible');
      return;
    }

    if (!projectId) {
      console.error('Project ID no está disponible');
      this.closeStatusModalWithError('Error: Project ID no disponible');
      return;
    }

    this.statusModalType = 'loading';
    this.statusModalMessage = 'Agendando cita!';
    this.showStatusModal = true;

    this.loading = true;
    const selectedProject = this.projects.find(p => p.projectId === projectId);
    const where = selectedProject?.location || '';
    const start = this.formatDateToTimekit(this.selectedEvent.start);
    const end = this.formatDateToTimekit(this.selectedEvent.end);

    const customer = {
      name: this.bookingForm.value.customerName,
      email: this.bookingForm.value.customerEmail,
      clientId: this.bookingForm.value.customerNationalId,
      phone: this.bookingForm.value.customerPhone,
    };

    const what = customer.name;

    this.apiService.createBookings(shopId, resourceId, projectId, start, end, customer, what, where)
      .then(async (response: any) => {
        let parsed: any;
        if (typeof response?.body === 'string') {
          try {
            parsed = JSON.parse(response.body);
          } catch {
            parsed = response.body;
          }
        } else if (response?.body) {
          parsed = response.body;
        } else {
          parsed = response;
        }

        const status = response?.statusCode ?? response?.status ?? 200;
        if (status === 200 && parsed?.data?.state === 'confirmed') {
          const sessionId = parsed.data.id;
          const selectedProject = this.projects.find(p => p.projectId === projectId);
          const timekitProjectId = selectedProject?.timekitProjectId;

          if (typeof timekitProjectId !== 'number') {
            console.error('Invalid timekitProjectId:', timekitProjectId);
            return;
          }

          if (selectedProject?.projectType?.toLowerCase().trim() === 'private') {
            try {
              if (!this.selectedClient) {
                console.error('No hay cliente seleccionado para sesión privada');
                return;
              }

              const nationalId = this.selectedClient.nationalId || customer.clientId;

              const counter = 0;
              const amount = 20;
              const searchString = '';

              const salesResponse: any = await this.lambdaService.getClientSales(
                nationalId ?? '0',
                counter,
                amount,
                searchString,
                shopId
              );

              const clientSales = Array.isArray(salesResponse)
                ? salesResponse
                : JSON.parse(salesResponse.body || '[]');

              if (clientSales.length === 0) {
                console.warn('El cliente no tiene ventas.');
                return;
              }

              let selectedSaleId = 0;
              let selectedItemId = 0;

              for (const sale of clientSales) {
                const saleId = sale.saleId;

                const orderDetailsResponse: any = await this.lambdaService.getUserSaleById(
                  saleId,
                  shopId
                );

                let orderDetails: any;

                if (Array.isArray(orderDetailsResponse)) {
                  orderDetails = orderDetailsResponse[0];
                } else if (orderDetailsResponse?.body) {
                  try {
                    const parsed = JSON.parse(orderDetailsResponse.body);
                    orderDetails = Array.isArray(parsed) ? parsed[0] : parsed;
                  } catch {
                    orderDetails = orderDetailsResponse;
                  }
                } else {
                  orderDetails = orderDetailsResponse;
                }

                const items = orderDetails?.items || [];

                const availableItem = items.find(
                  (it: any) => {
                    if (
                      typeof it.productSessions === 'number' &&
                      typeof it.bookingSessions === 'number' &&
                      typeof it.saleItemQuantity === 'number'
                    ) {
                      const totalSessionsForItem = it.productSessions * it.saleItemQuantity;
                      return it.bookingSessions < totalSessionsForItem;
                    }
                    return false;
                  }
                );

                if (availableItem) {
                  selectedSaleId = saleId;
                  selectedItemId = availableItem.itemId;
                  break;
                }
              }

              if (!selectedSaleId || !selectedItemId) {
                console.warn('No se encontraron ítems con sesiones disponibles.');
                this.closeStatusModalWithError('No hay sesiones disponibles para este cliente');
                return;
              }

              await this.apiService.createBookingSessions(
                shopId,
                selectedSaleId,
                selectedItemId,
                nationalId.toString(),
                sessionId
              );

            } catch (error) {
              console.error('Error creando sesión privada:', error);
            }
          }
          this.statusModalType = 'success';
          this.statusModalMessage = 'Cita agendada con éxito';

          setTimeout(() => {
            this.showStatusModal = false;
            this.cdr.detectChanges();
            this.getBookings();
          }, 2000);

        } else {
          console.error('Condición de éxito no cumplida:', { status, parsed });
          this.closeStatusModalWithError('Ha ocurrido un error al agendar la cita');
        }
      })
      .catch((error: any) => {
        this.loading = false;
        console.error('Error creating booking:', error);
        this.closeStatusModalWithError('Ha ocurrido un error al agendar la cita');
      });
  }

  formatDateLocalTime(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }


  blockWholeDay(start: Date, end: Date, dayOfWeek: string) {
    this.statusModalType = 'loading';
    this.statusModalMessage = 'Bloqueando todo el dia...';
    this.showStatusModal = true;

    const shopId = this.themingService.shopId;
    const projectId = this.selectedProjectId;
    const resourceId = this.selectedResourceId;
    const selectedProject = this.projects.find(p => p.projectId === projectId);
    const externalId = selectedProject?.externalId ?? '';

    const formattedStart = this.formatDateLocalTime(start);
    const formattedEnd = this.formatDateLocalTime(end);

    this.apiService.blockHours(
      shopId,
      projectId,
      resourceId,
      formattedStart,
      formattedEnd,
      externalId,
      dayOfWeek
    ).then(async (res: any) => {
      if ((res?.message === 'Blocked successfully')) {
        this.statusModalType = 'success';
        this.statusModalMessage = 'Dia bloqueado con éxito';
        setTimeout(() => {
          this.showStatusModal = false;
          this.cdr.detectChanges();
          this.getBookings();
        }, 2000);
      } else {
        this.closeStatusModalWithError('Error blocking day');
      }
    }).catch((err: any) => {
      console.error('Error blocking whole day:', err);
      this.closeStatusModalWithError('Error blocking day');
    });
  }

  blockHours() {
    if (!this.selectedEvent) {
      return;
    }

    this.statusModalType = 'loading';
    this.statusModalMessage = 'Bloqueando Horario...';
    this.showStatusModal = true;

    this.loading = true;
    const shopId = this.themingService.shopId;
    const resourceId = this.selectedResourceId;
    const projectId = this.selectedProjectId;
    const selectedProject = this.projects.find(p => p.projectId === projectId);
    const start = this.formatToTimekitEventDate(this.selectedEvent.start);
    const end = this.formatToTimekitEventDate(this.selectedEvent.end);
    const externalId = selectedProject?.externalId!;
    const dayOfWeek = this.selectedEvent.dayOfWeek;

    this.apiService.blockHours(shopId, projectId, resourceId, start, end, externalId, dayOfWeek)
      .then(async (response: any) => {
        this.loading = false;
        if ((response?.message === 'Blocked successfully')) {
          this.statusModalType = 'success';
          this.statusModalMessage = 'Horario bloqueado con éxito';
          this.showModal = false;

          setTimeout(() => {
            this.showStatusModal = false;
            this.cdr.detectChanges();
            this.getBookings();
          }, 2000);
        } else {
          this.closeStatusModalWithError('Error blocking hours');
        }
      })
      .catch((error: any) => {
        this.loading = false;
        console.error('Error blocking hours:', error);
        this.closeStatusModalWithError('Ha ocurrido un error al agendar la cita');
      });
  }

  onModalClosed(): void {
    this.showStatusModal = false;
  }

  closeStatusModalWithError(msg: string) {
    this.statusModalType = 'success';
    this.statusModalMessage = msg;
    setTimeout(() => {
      this.showStatusModal = false;
      this.cdr.detectChanges();
    }, 2500);
  }

  showSuccessModal(): void {
    this.isSuccessModalVisible = true;
    this.isErrorModalVisible = false;
  }

  showErrorModal(): void {
    this.isErrorModalVisible = true;
    this.isSuccessModalVisible = false;
  }

  closeModal(): void {
    this.showModal = false;
    this.isErrorModalVisible = false;
    this.isSuccessModalVisible = false;
  }

  handleCancelConfirmation(confirmed: boolean) {
    this.modalCancelHours = false;

    if (confirmed && this.pendingBlockData) {
      const { start, end, dayOfWeek } = this.pendingBlockData;
      this.blockWholeDay(start, end, dayOfWeek);
      this.pendingBlockData = null;
    }
  }
}
